import React, { useState } from "react";
import { Copy, QrCode, Upload } from "lucide-react";
import { AppButton, AppCard, AppCardContent, IconButton, cn } from "../ui";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

/**
 * The institute's UPI QR, shown on the page — not only inside a dialog behind a
 * Pay button. A student who has to pay by QR sees where to pay the moment they
 * arrive, scans it, then uploads the proof.
 *
 * Two layouts: `row` (QR beside the text — My Fees) and `stack` (QR above —
 * a course's narrow enrolment card).
 *
 * Props:
 *   config      — from /qr-payments/config
 *   amount      — shown large when the amount is fixed (an enrolment)
 *   blurb       — the line under the heading
 *   ctaLabel    — the upload button's words
 *   onUpload    — opens the proof dialog
 */
export default function QrPayCard({
  config, amount, blurb, ctaLabel = "I've paid — upload proof", onUpload, layout = "row", disabled = false,
}) {
  const [copied, setCopied] = useState(false);
  if (!config?.enabled) return null;
  const stack = layout === "stack";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(config.upiId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* blocked — the ID is on screen */ }
  };

  return (
    <AppCard as="section" aria-label="Pay by UPI QR">
      <AppCardContent
        className={cn(
          "tw-flex tw-gap-4 tw-px-5 tw-py-4",
          stack ? "tw-flex-col tw-items-center tw-text-center" : "tw-flex-wrap tw-items-center",
        )}
      >
        {config.qrImageUrl ? (
          <img
            src={config.qrImageUrl}
            alt={`UPI QR code${config.payeeName ? ` for ${config.payeeName}` : ""}`}
            className={cn(
              "tw-shrink-0 tw-rounded-md tw-border tw-border-solid tw-border-line",
              stack ? "tw-w-full tw-max-w-[13rem]" : "tw-w-36",
            )}
          />
        ) : (
          <span className="tw-inline-flex tw-h-14 tw-w-14 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-bg-primary/10 tw-text-primary">
            <QrCode size={26} />
          </span>
        )}
        <div className={cn("tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1.5", stack ? "tw-items-center" : "tw-min-w-[14rem]")}>
          <h2 className="tw-m-0 tw-text-[15px] tw-font-semibold tw-text-content">
            {amount != null ? `Pay ${money(amount)} by UPI` : "Pay by UPI"}
            {config.payeeName ? ` to ${config.payeeName}` : ""}
          </h2>
          <p className="tw-m-0 tw-text-[13px] tw-text-content-muted">
            {blurb || `${config.qrImageUrl ? "Scan this QR with any UPI app" : "Pay to this UPI ID from any UPI app"}, then upload the screenshot.`}
          </p>
          {config.upiId && (
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-1.5 tw-text-[13px]">
              <span className="tw-text-content-muted">UPI ID</span>
              <code className="tw-rounded-sm tw-bg-muted tw-px-1.5 tw-py-0.5 tw-text-[13px] tw-text-content">{config.upiId}</code>
              <IconButton label={copied ? "Copied" : "Copy UPI ID"} icon={Copy} size="xs" variant="ghost" onClick={copy} />
            </div>
          )}
          {config.instructions && (
            <p className="tw-m-0 tw-whitespace-pre-line tw-text-xs tw-text-content-muted">{config.instructions}</p>
          )}
          <div className={cn("tw-mt-1", stack && "tw-w-full")}>
            <AppButton onClick={onUpload} disabled={disabled} className={cn(stack && "tw-w-full")}>
              <Upload size={15} /> {ctaLabel}
            </AppButton>
          </div>
        </div>
      </AppCardContent>
    </AppCard>
  );
}
